import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { redirect } from 'react-router-dom'

import translationKeys from '@translationKeys'

import CustomPopover from '@components/CustomPopover'
import LogoutIcon from '@components/icons/LogoutIcon'
import SettingsIcon from '@components/icons/SettingsIcon'
import authApi from '@modules/auth/api'
import { User } from '@modules/auth/hooks/types'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, useMediaQuery, useTheme } from '@mui/material'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import { useMutation } from '@tanstack/react-query'

import {
  Root,
  StyledAvatar,
  StyledPendoGuideIcon,
  UserProfileButton,
  UserProfilePopoverInner,
  UserProfilePopoverLink,
  UserProfilePopoverLinkText,
  UserProfilePopoverMenu,
} from './index.styles'

export type UserProfileSectionProps = {
  className?: string
  hidePendoGuideIcon?: boolean
  user: User
}

const UserProfileSection = ({ className, user, hidePendoGuideIcon }: UserProfileSectionProps) => {
  const { t } = useTranslation()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)

  const theme = useTheme()

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleButtonClick = (e: React.MouseEvent<HTMLDivElement>) => {
    setAnchor(e.currentTarget as HTMLDivElement)
  }

  const { mutate: logout } = useMutation({
    mutationFn: authApi.logout.fn,
    onSuccess: () => {
      redirect('/sign-in')
      window.location.reload()
    },
  })

  const initials = [user.firstName, user.lastName]
    .filter(Boolean)
    .map(str => (str as string)[0])
    .join('')

  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ')

  return (
    <Root
      className={className}
      onKeyPress={(event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
          // @ts-expect-error onKeyPress is keyboard event handler and onDetailsClick expects mouse event
          handleButtonClick(event)
        }
      }}
    >
      <Box display="flex" alignItems="center">
        {!hidePendoGuideIcon && <StyledPendoGuideIcon id="pendo-guid-icon" />}

        <UserProfileButton onClick={handleButtonClick}>
          <StyledAvatar className="user-profile-button-image" alt="User avatar" src={user.profilePicture}>
            {initials}
          </StyledAvatar>
          {anchor ? (
            <ExpandLessIcon className="user-profile-button-arrow" />
          ) : (
            <ExpandMoreIcon className="user-profile-button-arrow" />
          )}
        </UserProfileButton>
      </Box>
      <CustomPopover
        sx={{ marginTop: '10px' }}
        id="user-profile-popover"
        anchor={anchor}
        setAnchor={setAnchor}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <UserProfilePopoverInner>
          <Typography sx={{ fontWeight: '700', marginBottom: '5px' }}>{fullName}</Typography>
          <Divider sx={{ marginBottom: '10px' }} />
          <UserProfilePopoverMenu>
            {!isMobile && (
              <UserProfilePopoverLink href="/plus/settings">
                <SettingsIcon />
                <UserProfilePopoverLinkText>{t(translationKeys.SETTINGS)}</UserProfilePopoverLinkText>
              </UserProfilePopoverLink>
            )}
            <UserProfilePopoverLink onClick={logout}>
              <LogoutIcon />
              <UserProfilePopoverLinkText>{t(translationKeys.LOGOUT)}</UserProfilePopoverLinkText>
            </UserProfilePopoverLink>
          </UserProfilePopoverMenu>
        </UserProfilePopoverInner>
      </CustomPopover>
    </Root>
  )
}

export default UserProfileSection
